/**
 * mindermenge — Mindestbestellwert + Mindermengenzuschlag (reine Funktionen).
 *
 * Regel wird in Admin → PDF-Einstellungen → Dokument „Bestellung“ gepflegt
 * (pdf_settings.vorlagen.bestellung). Liegt der Netto-Warenwert einer
 * Bestellung (nach Zeilenrabatt und Kundenrabatt, ohne Zuschlag) unter dem
 * Mindestbestellwert, wird EINE Zuschlagszeile angefügt: Position ohne
 * Produkt, Menge 1, netto, mit eigenem Erlöskonto (Standard 4412). Die Zeile
 * läuft wie jede Position durch PDF, Rechnung und Buchung; Lager ignoriert
 * Positionen ohne product_id. Dieselbe Regel gilt im Kundenportal (DB-Funktion
 * mindermenge_regel()).
 */
import { getVorlage, renderPlatzhalter } from './dokumentVorlagen';

export const MINDERMENGE_ZEILEN_TYP = 'mindermengenzuschlag';
export const MINDERMENGE_SKU = 'MINDERMENGE';
const STANDARD_KONTO = '4412';

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const num = (v) => {
  if (v == null || v === '') return 0;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const eur = (n) => round2(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** @returns {{ mindestbestellwert:number, zuschlag:number, konto:string, text:string }} */
export function leseMindermengeRegel(settings) {
  const v = getVorlage(settings || {}, 'bestellung');
  return {
    mindestbestellwert: Math.max(0, num(v.mindestbestellwert)),
    zuschlag: Math.max(0, num(v.mindermengenzuschlag)),
    konto: (v.zuschlag_konto || '').trim() || STANDARD_KONTO,
    text: v.zuschlag_text || 'Mindermengenzuschlag (Warenwert unter {{bestellung.mindestbestellwert}} € netto)',
  };
}

export const isZuschlagZeile = (it) => !!it && (it.zeilen_typ === MINDERMENGE_ZEILEN_TYP || it.product_sku === MINDERMENGE_SKU);

/** Netto-Warenwert der echten Positionen (Zeilenrabatt, dann Kundenrabatt). */
export function warenwertNetto(items, { kundenrabattProzent = 0 } = {}) {
  const sub = (items || []).filter((it) => !isZuschlagZeile(it)).reduce((s, it) => {
    const rabatt = Math.min(100, Math.max(0, num(it.rabatt_prozent)));
    const price = it.discounted_unit_price != null && it.erhoehung_prozent == null
      ? num(it.discounted_unit_price)
      : num(it.unit_price) * (1 - rabatt / 100);
    return s + num(it.quantity) * price;
  }, 0);
  const kr = Math.min(100, Math.max(0, num(kundenrabattProzent)));
  return round2(sub * (1 - kr / 100));
}

/** @returns {{ faellig:boolean, betrag:number, fehlbetrag:number }} */
export function berechneZuschlag(warenwert, regel) {
  const min = num(regel?.mindestbestellwert);
  const zuschlag = round2(num(regel?.zuschlag));
  const fehlbetrag = min > 0 && warenwert < min ? round2(min - warenwert) : 0;
  const faellig = fehlbetrag > 0 && zuschlag > 0;
  return { faellig, betrag: faellig ? zuschlag : 0, fehlbetrag };
}

/** Die Zuschlagszeile als Auftragsposition (ohne Produkt, netto, Konto). */
export function zuschlagZeile(regel) {
  const betrag = round2(num(regel.zuschlag));
  const name = renderPlatzhalter(regel.text, { 'bestellung.mindestbestellwert': eur(regel.mindestbestellwert) });
  return {
    zeilen_typ: MINDERMENGE_ZEILEN_TYP,
    product_id: null,
    product_name: name,
    product_sku: MINDERMENGE_SKU,
    variant: null,
    variant_value: null,
    variant_label: null,
    variant_sku: null,
    quantity: 1,
    unit: 'pauschal',
    unit_price: betrag,
    base_unit_price: betrag,
    discounted_unit_price: betrag,
    rabatt_prozent: 0,
    erhoehung_prozent: 0,
    total: betrag,
    revenue_account_no: regel.konto || STANDARD_KONTO,
  };
}

export function ohneZuschlag(items) {
  return (items || []).filter((it) => !isZuschlagZeile(it));
}

/** Positionen mit genau einer (oder keiner) Zuschlagszeile – idempotent, unveränderlich. */
export function mitZuschlag(items, regel, { kundenrabattProzent = 0 } = {}) {
  const basis = ohneZuschlag(items);
  const { faellig } = berechneZuschlag(warenwertNetto(basis, { kundenrabattProzent }), regel);
  return faellig ? [...basis, zuschlagZeile(regel)] : basis;
}

/** Summen einer Positionsliste (netto, MwSt, brutto) – für Angebot/Bestellung. */
export function summen(items, taxRate = 19) {
  const subtotal = round2((items || []).reduce((s, it) => s + num(it.total ?? num(it.quantity) * num(it.unit_price)), 0));
  const tax = round2(subtotal * (num(taxRate) / 100));
  return { subtotal, tax_amount: tax, total: round2(subtotal + tax) };
}
